import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpHandler, HttpHeaders, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Device, DeviceInfo } from '@capacitor/device';
import { Platform } from '@ionic/angular/lazy';
import { AuthService } from '@app/lib/b2b-services/auth/auth.service';
import { StringUtilService } from '@app/lib/b2b-services/wrapper/string-util.service';
import { environment } from '@src/environments/environment';
import { DateUtilService } from '@app/lib/b2b-services/util/date-util.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/lib/b2b-stores';
import * as fromUser from '@app/lib/b2b-stores/user';
import { ToastService } from '@app/lib/b2b-services/alert/toast-service';
import { ERROR_MESSAGES } from '@app/lib/b2b-constants/messages.constant';
import { RouterUtilService } from '@app/lib/b2b-services/util/router-util.service';

@Injectable({
  providedIn: 'root',
})
export class NoopHttpInterceptorService {
  deviceInfo: DeviceInfo;
  private isRefreshing = Boolean(false);
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private authService: AuthService,
    private stringUtilService: StringUtilService,
    private platform: Platform,
    private store: Store<fromRoot.State>,
    private toastService: ToastService,
    private routerUtilService: RouterUtilService,
  ) {
    this.init().then();
  }

  async init() {
    this.deviceInfo = await Device.getInfo();
    // LoggerService.log('this.deviceInfo ', this.deviceInfo);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // LoggerService.log('Inside interceptor req ', req?.url);
    let newReq: HttpRequest<any> = req;

    if (
      !req?.url?.includes('auth-service/dashboard/login') &&
      !req?.url?.includes('auth-service/dashboard/generate-otp') &&
      !req?.url?.includes('auth-service/dashboard/forgot-password') &&
      !req?.url?.includes('auth-service/dashboard/reset-password') &&
      !req?.url?.includes('auth-service/anonymous-user') &&
      !req?.url?.includes('auth-service/update-password')
    ) {
      newReq = this.getNewRequest(req);
    }

    return next.handle(newReq).pipe(
      catchError((error: HttpErrorResponse) =>
        // LoggerService.error(error);
        // LoggerService.log('newReq is ', newReq);
        this.handleHttpRequestError(newReq, next, error, this.isRefreshing),
      ),
    );
  }

  private handleHttpRequestError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
    afterRefresh: boolean,
  ): Observable<any> {
    // LoggerService.log('Inside handleHttpRequestError and error is ', error);
    switch (error.status) {
      case 401:
        // LoggerService.log('Inside 401 case and req.url is ', req.url);
        if (req.url.indexOf('/auth-service/dashboard/login') > -1) {
          // LoggerService.log('401 if block');
          return throwError(error);
        } else {
          // LoggerService.log('401 else block');
          if (afterRefresh) {
            // LoggerService.log('401 else if block');
            this.logout();
            this.routerUtilService.navigateByUrl('/auth/login').then();
            return throwError(error);
          } else {
            // LoggerService.log('401 else else block');
            return this.handle401Error(req, next);
          }
        }
      // break;
      case 403:
        // this.logout();
        // TODO: Don't logout. show error message to the user
        return throwError(error);
      // break;
      case 500:
        return throwError(error);
      // break;
      case 404:
        return throwError(error);
      // break;
      case 0:
        this.toastService.error(ERROR_MESSAGES.SERVER_IS_DOWN);
        return throwError(error);
      // break;
      default:
        return throwError(error);
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    // LoggerService.log('INside handle401Error');
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken: string = this.authService.refreshToken;
      const authToken: string = this.authService.authToken;
      const accessToken: string = this.authService.accessToken;
      // LoggerService.log('refreshToken 1 ', refreshToken);
      if (refreshToken) {
        // LoggerService.log('before calling create refresh token');
        return this.authService
          .createRefreshToken(refreshToken, authToken, accessToken)
          .pipe(
            switchMap(
              (result: {
                token: string;
                refreshToken: string;
                accessToken: string;
              }) => {
                this.isRefreshing = false;
                this.refreshTokenSubject.next(result.token);
                // console.log('Inside createRefreshToken new token is ', result);
                this.authService.saveAuthAndRefreshToken(result);
                /*console.log(
                'next.handle(this.getNewRequest(request)) inside switchmap ',
                this.getNewRequest(request)
              );*/
                return next.handle(this.getNewRequest(request));
              },
            ),
            catchError((err: HttpErrorResponse) => {
              // console.log('Inside createRefreshToken catchError ', err);
              this.isRefreshing = false;
              this.handleHttpRequestError(request, next, err, true);
              return throwError(err);
            }),
          );
      }
    }
    return this.refreshTokenSubject.pipe(
      filter(
        (token: string) =>
          // console.log('Inside refreshTokenSubject filter and token is ', token);
          token !== null,
      ),
      take(1),
      switchMap(
        (
          token: string /*console.log(
            'next.handle(this.getNewRequest(request)) inside refreshTokenSubject pipe switchmap ',
            this.getNewRequest(request)
          );*/,
        ) =>
          // console.log('new token is ', token);
          next.handle(this.getNewRequest(request)),
      ),
      /*catchError((err: HttpErrorResponse) => {
        this.handleHttpRequestError(request, next, err, true);
        this.isRefreshing = false;
        return throwError(err);
      })*/
    );
  }

  private logout() {
    this.store.dispatch(fromUser.SignOut({}));
  }

  private getNewRequest(req: HttpRequest<any>): HttpRequest<any> {
    let newReq: HttpRequest<any> = req;
    // console.error(' getNewRequest ', this.authService.authToken);
    newReq = req.clone({
      headers: this.getUpdatedHeaders(req, this.authService.authToken),
    });

    return newReq;
  }

  private getUpdatedHeaders(req: HttpRequest<any>, at: string): HttpHeaders {
    return req.headers
      .set('Authorization', this.verifyHeaderValue(at))
      .set('x-platform', this.verifyHeaderValue(this.deviceInfo?.platform))
      .set(
        'x-web-view-version',
        this.verifyHeaderValue(this.deviceInfo?.webViewVersion),
      )
      .set('x-device-model', this.verifyHeaderValue(this.deviceInfo?.model))
      .set('x-os', this.verifyHeaderValue(this.deviceInfo?.operatingSystem))
      .set('x-os-version', this.verifyHeaderValue(this.deviceInfo?.osVersion))
      .set(
        'x-manufacturer',
        this.verifyHeaderValue(this.deviceInfo?.manufacturer),
      )
      .set('x-app-version', environment?.appVersion)
      .set(
        'x-timezone',
        this.verifyHeaderValue(DateUtilService.getTimezoneOffset()),
      )
      .set('x-device-width', this.platform.width() + '')
      .set('x-device-height', this.platform.height() + '')
      .set('x-app-type', 'dashboard');
  }

  private verifyHeaderValue(val: string) {
    if (this.stringUtilService.isEmpty(val)) {
      return '';
    } else {
      return val;
    }
  }
}
